import React from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { formatOverageRate, matchGuideRow, weightCap } from './bagSizeRules';

/**
 * PieceServiceRow — Row for per-piece services (inputWeight=false).
 *
 * Shows service name, price per piece, and +/- counter buttons.
 */
export default function PieceServiceRow({ service, cartItem, dispatch, bagSizeGuide = [], bagOverageRate }) {
  const quantity = cartItem ? cartItem.quantity : 0;
  const bagRow = matchGuideRow(service.serviceName, bagSizeGuide);
  const cap = weightCap(bagRow);
  const overageLabel = formatOverageRate(bagOverageRate);
  const isBag = cap > 0 || /bag|gallon/i.test(service.serviceName || '');

  const handleIncrement = () => {
    if (cartItem) {
      dispatch({
        type: 'UPDATE_QUANTITY',
        serviceId: service.serviceId,
        quantity: quantity + 1,
      });
    } else {
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          serviceId: service.serviceId,
          serviceName: service.serviceName,
          categoryId: service.categoryId,
          categoryName: service.categoryName,
          price: parseFloat(service.price),
          inputWeight: false,
          quantity: 1,
          // Carry turnaround so Schedule can enforce the earliest return date.
          turnaroundHours: (service.turnaroundHours != null) ? service.turnaroundHours : null,
          isDryClean: !!service.isDryClean,
          // Carry bedding flags so the cart applies the "1st full / each
          // additional" ringing (option A). Missing/false => normal pricing.
          isBedding: !!service.isBedding,
          beddingAdditionalPrice: (service.beddingAdditionalPrice != null) ? service.beddingAdditionalPrice : 20,
        },
      });
    }
  };

  const handleDecrement = () => {
    if (!cartItem) return;
    const newQty = quantity - 1;
    if (newQty <= 0) {
      dispatch({
        type: 'UPDATE_QUANTITY',
        serviceId: service.serviceId,
        quantity: 0,
      });
    } else {
      dispatch({
        type: 'UPDATE_QUANTITY',
        serviceId: service.serviceId,
        quantity: newQty,
      });
    }
  };

  return (
    <Box
      p={3}
      borderRadius="xl"
      border="2px solid"
      borderColor={quantity > 0 ? 'blue.400' : 'gray.200'}
      bg={quantity > 0 ? 'blue.50' : 'white'}
      transition="all 0.2s"
    >
      <Flex justify="space-between" align="center">
        <Box flex="1" pr={2} minW={0}>
          {/* Larger, wrapping name so long service names are readable; hover
              shows the full name (helps when it's still long on small screens). */}
          <Tooltip label={service.serviceName} placement="top" openDelay={300} hasArrow>
            <Text fontWeight="700" fontSize="md" color="gray.800" whiteSpace="normal" wordBreak="break-word">
              {service.serviceName}
            </Text>
          </Tooltip>
          {service.description && service.description !== 'N/A' && (
            <Text fontSize="xs" color="gray.500" whiteSpace="normal" wordBreak="break-word">
              {service.description}
            </Text>
          )}
          <Text fontSize="md" fontWeight="800" color="blue.600" mt={1}>
            ${parseFloat(service.price).toFixed(2)}{isBag ? '/bag' : '/item'}
          </Text>
          {cap > 0 ? (
            <Text fontSize="xs" color="gray.600">
              Includes up to {cap} lb{overageLabel ? `. Extra ${overageLabel}` : ''}
            </Text>
          ) : (
            <Text fontSize="xs" color="gray.500">
              {isBag ? 'Price per bag' : 'Price per item'}
            </Text>
          )}
          {bagRow?.capacity ? (
            <Text fontSize="xs" color="gray.500">{bagRow.capacity}</Text>
          ) : null}
        </Box>
        <HStack spacing={2}>
          {quantity > 0 && (
            <IconButton
              icon={<FaMinus />}
              aria-label="Decrease"
              size="sm"
              borderRadius="full"
              colorScheme="blue"
              variant="outline"
              onClick={handleDecrement}
            />
          )}
          {quantity > 0 && (
            <Text fontWeight="bold" fontSize="lg" minW="24px" textAlign="center">
              {quantity}
            </Text>
          )}
          <IconButton
            icon={<FaPlus />}
            aria-label={quantity > 0 ? 'Increase' : 'Add'}
            size="sm"
            borderRadius="full"
            colorScheme="blue"
            variant={quantity > 0 ? 'outline' : 'solid'}
            onClick={handleIncrement}
          />
        </HStack>
      </Flex>
      {quantity > 0 && (
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {quantity} × ${parseFloat(service.price).toFixed(2)} = $
          {(quantity * parseFloat(service.price)).toFixed(2)}
        </Text>
      )}
    </Box>
  );
}
